import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import { OVERLAY_QR, BACKGROUND_PREVIEW_QR_PX } from "../../shared/qr-render";
import { CANVAS_DEFAULT_FONT_FAMILY } from "../../shared/font-presets";

@Component({
  selector: "app-background-preview",
  templateUrl: "./background-preview.component.html",
  styleUrls: ["./background-preview.component.scss"],
  standalone: false,
})
export class BackgroundPreviewComponent
  implements AfterViewInit, OnChanges, OnDestroy
{
  private static readonly CANVAS_WIDTH = 1280;
  private static readonly CANVAS_HEIGHT = 720;
  readonly fallbackQrData =
    globalThis.location?.origin || "https://gsignature.com";
  readonly overlayQr = OVERLAY_QR;
  readonly previewQrPx = BACKGROUND_PREVIEW_QR_PX;

  @ViewChild("previewStage", { static: true })
  private previewStageRef!: ElementRef<HTMLDivElement>;

  @Input() bannerType: "gradient" | "image" = "gradient";
  @Input() bannerGradientStart = "#ffffff";
  @Input() bannerGradientEnd = "#f5f5f0";
  @Input() bannerImageUrl: string | null = null;
  @Input() showLogo = true;
  @Input() hasQr = false;
  @Input() logoUrl: string | null = null;
  @Input() qrLabel: string | null = null;
  @Input() qrData: string | null = null;
  @Input() fontFamily = CANVAS_DEFAULT_FONT_FAMILY;
  @Input() fontCssUrl: string | null = null;
  @Input() companyFontFileUrl: string | null = null;
  @Input() loading = false;
  canvasScale = 1;

  private resizeObserver: ResizeObserver | null = null;
  private injectedFontNode: HTMLElement | null = null;

  get canvasStyle(): Record<string, string> {
    if (this.bannerType === "image" && this.bannerImageUrl) {
      return {
        backgroundImage: `url(${this.bannerImageUrl})`,
        backgroundSize: "100% 100%",
        backgroundPosition: "0 0",
      };
    }
    return {
      background: `linear-gradient(135deg, ${this.bannerGradientStart || "#ffffff"} 0%, ${this.bannerGradientEnd || "#f5f5f0"} 100%)`,
    };
  }

  get canvasTransform(): string {
    return `translate(-50%, -50%) scale(${this.canvasScale})`;
  }

  get labelFontFamily(): string {
    return this.fontFamily?.trim() || CANVAS_DEFAULT_FONT_FAMILY;
  }

  ngAfterViewInit(): void {
    this.recalculateScale();
    this.resizeObserver = new ResizeObserver(() => this.recalculateScale());
    this.resizeObserver.observe(this.previewStageRef.nativeElement);
    this.syncPreviewFontFace();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes["fontFamily"] ||
      changes["fontCssUrl"] ||
      changes["companyFontFileUrl"]
    ) {
      this.syncPreviewFontFace();
    }
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    this.injectedFontNode?.remove();
    this.injectedFontNode = null;
  }

  @HostListener("window:resize")
  onWindowResize(): void {
    this.recalculateScale();
  }

  private syncPreviewFontFace(): void {
    const cssUrl = this.fontCssUrl?.trim() || "";
    const companyUrl = this.companyFontFileUrl?.trim() || "";
    const family = (this.fontFamily.split(",")[0] ?? "")
      .trim()
      .replace(/^["']+|["']+$/g, "");
    this.injectedFontNode?.remove();
    this.injectedFontNode = null;
    if (cssUrl) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = cssUrl;
      link.setAttribute("data-bg-preview-font", "1");
      document.head.appendChild(link);
      this.injectedFontNode = link;
      return;
    }
    if (companyUrl && family) {
      const style = document.createElement("style");
      style.setAttribute("data-bg-preview-font", "1");
      style.textContent = `@font-face{font-family:"${family.replace(/"/g, "")}";src:url("${companyUrl}");font-display:swap;}`;
      document.head.appendChild(style);
      this.injectedFontNode = style;
    }
  }

  private recalculateScale(): void {
    const stage = this.previewStageRef.nativeElement;
    const { width, height } = stage.getBoundingClientRect();
    const styles = window.getComputedStyle(stage);
    const paddingX =
      (parseFloat(styles.paddingLeft || "0") || 0) +
      (parseFloat(styles.paddingRight || "0") || 0);
    const paddingY =
      (parseFloat(styles.paddingTop || "0") || 0) +
      (parseFloat(styles.paddingBottom || "0") || 0);
    const innerWidth = Math.max(0, width - paddingX);
    const innerHeight = Math.max(0, height - paddingY);
    if (innerWidth <= 0 || innerHeight <= 0) {
      this.canvasScale = 1;
      return;
    }
    const safeWidth = Math.max(0, innerWidth - 2);
    const safeHeight = Math.max(0, innerHeight - 2);
    const scaleX = safeWidth / BackgroundPreviewComponent.CANVAS_WIDTH;
    const scaleY = safeHeight / BackgroundPreviewComponent.CANVAS_HEIGHT;
    this.canvasScale = Math.max(0.05, Math.min(scaleX, scaleY, 1));
  }
}
